import { Trans, useTranslation } from 'react-i18next';

import { Stack } from '@hotjar/hotsauce';

import { trafficCoverageLevelToShortI18nKeyMap } from '@hotjar/webapp/domains/revenue/sampling/constants';
import type { TrafficCoverageLevel } from '@hotjar/webapp/domains/revenue/sampling/types';

import { FeatureBarStyles as S } from './FeatureBar.styles';

interface FeatureBarProps {
  coverageLevel: TrafficCoverageLevel;
  title: string;
  description: string;
  illustration: string;
}

export const FeatureBar = ({
  coverageLevel,
  title,
  description,
  illustration,
}: FeatureBarProps): JSX.Element => {
  const { t: tCommon } = useTranslation('common');
  const { t: tRevenue } = useTranslation('revenue', { keyPrefix: 'plan-comparison-modal' });

  /**
   * We provide these values to the title and description translation strings, so that they can interpolate
   * these values into the translations.
   *
   * For example, you could declare a translation string as: `You have {{trafficCoverage}} traffic coverage.`.
   * And then if we declare a `trafficCoverage` property in the `i18nContext`, it can interpolate it automatically.
   *
   * Translation strings don't have to use all values available. Unused values are simply ignored.
   */
  const i18nContext = {
    trafficCoverage: tCommon(trafficCoverageLevelToShortI18nKeyMap[coverageLevel]),
  };

  return (
    <Stack direction="row" spacing="x7pt5">
      <div>
        <S.Illustration src={illustration} alt={tRevenue(title)} />
      </div>
      <S.ContentContainer>
        <Stack direction="column" spacing="x2pt5">
          <div>
            <S.FeatureTitle>{tRevenue(title)}</S.FeatureTitle>
            <S.FeatureDescription>
              {description && (
                <Trans
                  i18nKey={`revenue:plan-comparison-modal.${description}`}
                  tOptions={{ ...i18nContext }}
                  components={[<S.ListItem />]}
                />
              )}
            </S.FeatureDescription>
          </div>
        </Stack>
      </S.ContentContainer>
    </Stack>
  );
};
